import { ToasterService } from '@abp/ng.theme.shared';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormButtonConfiguration } from 'src/ca-shared/form/form-button-configuration.model';
import { ConfigStateService, LocalizationService, PermissionService } from '@abp/ng.core';
import { SettingConstants } from 'src/core/constants/setting-constant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConversationService } from 'src/core/services/conversation/conversation.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';
import { ConversationExcelDetailDto } from 'src/core/models/conversation/conversation-excel-detail.dto';
import { Store } from '@ngxs/store';
import { ConversationDetailState } from 'src/core/states/conversation/conversation-detail.state';
import { ExcelDetailChange } from 'src/core/actions/conversation/conversation-detail.actions';
import { DatePipe } from '@angular/common';
import { CrudService } from 'src/core/services/crud/crud.service';
import { CategoryDto } from 'src/core/models/query/category.dto';
import { CategoryType } from 'src/core/models/enum/category-type.enum';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { InsightType } from 'src/core/models/generic-lookup-type/insight/insight-type.glt';
import { map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ConversationExportPreferenceState } from 'src/core/states/conversation/conversation-export-preference.state';
import { ConversationExportCategorySelectionChange } from 'src/core/actions/conversation/conversation-export-preference.actions';
import { ConversationExportCategorySelectionModel } from 'src/core/models/conversation/conversation-export-category-selection.model';

@Component({
  selector: 'ca-conversation-export',
  templateUrl: './conversation-export.component.html',
  styleUrls: ['./conversation-export.component.scss'],
  standalone: false,
})
export class ConversationExportComponent implements OnInit, OnDestroy {
  @ViewChild('conversationResultExportModal') conversationResultExportModal: any;

  @Input()
  isDisabled: boolean = false;

  canExportConversation: boolean = false;
  isSentimentEnabled: boolean = false;
  form: FormGroup;
  documentName: string = '';
  userDefinedCategories: CategoryDto[] = [];
  AIGeneratedCategories: CategoryDto[] = [];
  aiInsightTypes: GenericLookupDto[] = [];
  aiInsightTypesRows: GenericLookupDto[][] = [];
  categories$: Observable<CategoryDto[]>;

  private exportFingerprint: string = null;
  private readonly destroy$ = new Subject<void>();

  get formButtonConfiguration(): FormButtonConfiguration {
    const result: FormButtonConfiguration = {
      save: {
        name: this.localizationService.instant('::Export'),
        hideIcon: true,
        visible: true,
        tooltip: null,
        disabled: false,
      },
      delete: {
        visible: false,
        tooltip: null,
        disabled: false,
      },
    };

    return result;
  }

  get userDefinedCategoryCount(): string {
    return this.userDefinedCategories.length.toString();
  }

  get aiGeneratedCategoryCount(): string {
    return this.AIGeneratedCategories.length.toString();
  }

  get isIncludeUserDefinedCategoriesChecked(): boolean {
    return !!this.form.get('includeUserDefinedCategories').value;
  }

  get isUserDefinedCategorySelectionVisible(): boolean {
    return this.isIncludeUserDefinedCategoriesChecked && this.userDefinedCategories.length > 0;
  }

  get selectedUserDefinedCategoryCount(): string {
    return this.getSelectedActiveUserDefinedCategoryIds().length.toString();
  }

  get activeUserDefinedCategoryCount(): string {
    return this.getActiveUserDefinedCategoryIds().length.toString();
  }

  constructor(
    private permissionService: PermissionService,
    private conversationService: ConversationService,
    private toastr: ToasterService,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private localizationService: LocalizationService,
    private store: Store,
    private datePipe: DatePipe,
    private service: CrudService,
    private operators: Operators,
    private config: ConfigStateService,
  ) {
    this.canExportConversation = this.permissionService.getGrantedPolicy(
      'Conversation.ConversationExport',
    );
    this.isSentimentEnabled =
      this.config.getSetting(SettingConstants.SentimentEnabled)?.toLowerCase() === 'true';

    let filters: FilterItemDto[] = [
      {
        field: 'isActive',
        operator: this.operators.Equals,
        value: true,
      },
      {
        field: 'isCategory',
        operator: this.operators.Equals,
        value: true,
      },
    ];

    this.categories$ = this.service
      .get<CategoryDto>(CategoryDto, {
        maxResultCount: 9999,
        skipCount: 0,
        filters: filters,
        sorters: [],
      })
      .pipe(
        map(response => response.items),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.categories$.pipe(takeUntil(this.destroy$)).subscribe(categories => {
      this.userDefinedCategories = [
        ...categories.filter(x => x.categoryType == CategoryType.Query),
      ];
      this.AIGeneratedCategories = [
        ...categories.filter(x => x.categoryType == CategoryType.Topic),
      ];
    });

    this.form = this.fb.group({
      documentName: [
        null,
        {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(250),
            StringValidator.whitespace,
          ],
        },
      ],
      includeUserDefinedCategories: [false],
      includeAIGeneratedCategories: [false],
      includeAIInsights: [false],
      selectedAIInsightTypes: [[]],
      includeSentimentValues: [false],
      selectedUserDefinedCategories: [[]],
    });

    this.form.get('includeAIInsights').valueChanges.subscribe(value => {
      if (value) {
        const currentSelection = this.form.get('selectedAIInsightTypes').value || [];
        if (!currentSelection || currentSelection.length === 0) {
          this.form.get('selectedAIInsightTypes').setValue(this.aiInsightTypes.map(x => x.code));
        }
      } else {
        this.form.get('selectedAIInsightTypes').setValue([]);
      }
    });

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(
        map(filterFn => filterFn(InsightType)),
        take(1),
      )
      .subscribe(response => {
        this.aiInsightTypes = response || [];
        this.buildAIInsightRows();

        if (this.form.get('includeAIInsights').value) {
          const currentSelection = this.form.get('selectedAIInsightTypes').value || [];
          if (!currentSelection || currentSelection.length === 0) {
            this.form.get('selectedAIInsightTypes').setValue(this.aiInsightTypes.map(x => x.code));
          }
        }
      });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOpenModal() {
    this.modalService.open(this.conversationResultExportModal, {
      size: 'lg',
      scrollable: false,
      backdrop: 'static',
      keyboard: false,
    });

    const today = new Date();
    const dateStr = this.datePipe.transform(today, 'ddMMyyyy');
    let fileName = this.localizationService.instant('Conversation::ExcelExport.FileName');
    this.documentName = fileName + '-' + dateStr;

    this.getState();
    this.restoreUserDefinedCategorySelection();
  }

  onModalClosed() {
    this.modalService.dismissAll();
  }

  onExportExcelClick() {
    if (this.form.valid) {
      let excelDetail = this.form.value;
      let excelDetailDto: ConversationExcelDetailDto = {
        documentName: excelDetail.documentName,
        includeUserDefinedCategories: excelDetail.includeUserDefinedCategories,
        includeAIGeneratedCategories: excelDetail.includeAIGeneratedCategories,
        includeAIInsights: excelDetail.includeAIInsights,
        selectedAIInsightTypes: excelDetail.includeAIInsights
          ? excelDetail.selectedAIInsightTypes
          : [],
        includeSentimentValues: excelDetail.includeSentimentValues,
        selectedUserDefinedCategoryIds: this.getSelectedUserDefinedCategoryIdsForExport(
          excelDetail.includeUserDefinedCategories,
        ),
      };

      this.setState();
      this.saveUserDefinedCategorySelection(excelDetail.includeUserDefinedCategories);
      this.modalService.dismissAll();

      this.conversationService.exportList(excelDetailDto).subscribe(() => {
        this.toastr.success('Conversation::ExportListRequestSuccessful');
      });
    }
  }

  getState() {
    const excelDetail = this.store.selectSnapshot(ConversationDetailState.getExcelDetail);
    if (excelDetail) {
      this.form.get('documentName').setValue(excelDetail?.documentName ?? this.documentName);
      this.form
        .get('includeUserDefinedCategories')
        .setValue(excelDetail?.includeUserDefinedCategories);
      this.form
        .get('includeAIGeneratedCategories')
        .setValue(excelDetail?.includeAIGeneratedCategories);
      this.form.get('includeAIInsights').setValue(excelDetail?.includeAIInsights);
      this.form.get('selectedAIInsightTypes').setValue(excelDetail?.selectedAIInsightTypes || []);
      this.form
        .get('includeSentimentValues')
        .setValue(excelDetail?.includeSentimentValues ?? false);
    }
  }

  setState() {
    let excelDetail = this.form.value;
    let excelDetailDto: ConversationExcelDetailDto = {
      documentName:
        excelDetail.documentName === this.documentName ? null : excelDetail.documentName,
      includeUserDefinedCategories: excelDetail?.includeUserDefinedCategories ?? false,
      includeAIGeneratedCategories: excelDetail?.includeAIGeneratedCategories ?? false,
      includeAIInsights: excelDetail?.includeAIInsights ?? false,
      selectedAIInsightTypes:
        excelDetail?.includeAIInsights && excelDetail?.selectedAIInsightTypes
          ? excelDetail.selectedAIInsightTypes
          : [],
      includeSentimentValues: excelDetail?.includeSentimentValues ?? false,
    };
    const excelDetailChangedAction = new ExcelDetailChange(excelDetailDto);
    this.store.dispatch(excelDetailChangedAction);
  }

  aiInsightSelected(code: string): boolean {
    const selected = this.form.get('selectedAIInsightTypes').value || [];
    return selected.includes(code);
  }

  toggleAIInsightType(checked: boolean, code: string) {
    const selected = this.form.get('selectedAIInsightTypes').value || [];
    const currentSelection = [...selected];

    if (checked) {
      if (!currentSelection.includes(code)) {
        currentSelection.push(code);
      }
    } else {
      const index = currentSelection.indexOf(code);
      if (index > -1) {
        currentSelection.splice(index, 1);
      }
    }

    this.form.get('selectedAIInsightTypes').setValue(currentSelection);
  }

  private restoreUserDefinedCategorySelection(): void {
    this.exportFingerprint = this.conversationService.getExportFingerprint();
    const selectionEntry = this.store.selectSnapshot(
      ConversationExportPreferenceState.getCategorySelection,
    )(this.exportFingerprint);

    this.categories$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.form
        .get('selectedUserDefinedCategories')
        .setValue(this.buildRestoredUserDefinedCategorySelection(selectionEntry));
    });
  }

  private buildRestoredUserDefinedCategorySelection(
    selectionEntry: ConversationExportCategorySelectionModel | undefined,
  ): { id: number; name: string }[] {
    if (!selectionEntry) {
      return [];
    }

    const activeCategories = this.userDefinedCategories.filter(x => x.queryId != null);
    const restoredCategories = selectionEntry.allSelected
      ? activeCategories
      : activeCategories.filter(x => (selectionEntry.queryIds ?? []).includes(x.queryId));

    return restoredCategories.map(x => ({ id: x.queryId, name: x.name }));
  }

  private getActiveUserDefinedCategoryIds(): number[] {
    return this.userDefinedCategories.filter(x => x.queryId != null).map(x => x.queryId);
  }

  private getSelectedActiveUserDefinedCategoryIds(): number[] {
    const activeIds = new Set(this.getActiveUserDefinedCategoryIds());
    const selection: { id: number }[] = this.form.get('selectedUserDefinedCategories').value || [];
    const selectedIds = selection.filter(x => x != null && activeIds.has(x.id)).map(x => x.id);

    return Array.from(new Set(selectedIds));
  }

  private getSelectedUserDefinedCategoryIdsForExport(
    includeUserDefinedCategories: boolean,
  ): number[] | null {
    if (!includeUserDefinedCategories) {
      return null;
    }

    const activeIds = this.getActiveUserDefinedCategoryIds();
    const selectedIds = this.getSelectedActiveUserDefinedCategoryIds();

    if (selectedIds.length === 0 || selectedIds.length === activeIds.length) {
      return null;
    }

    return selectedIds;
  }

  private saveUserDefinedCategorySelection(includeUserDefinedCategories: boolean): void {
    if (!includeUserDefinedCategories) {
      return;
    }

    const fingerprint = this.exportFingerprint ?? this.conversationService.getExportFingerprint();
    const activeIds = this.getActiveUserDefinedCategoryIds();
    const selectedIds = this.getSelectedActiveUserDefinedCategoryIds();
    const allSelected = activeIds.length > 0 && selectedIds.length === activeIds.length;

    this.store.dispatch(
      new ConversationExportCategorySelectionChange(fingerprint, allSelected, selectedIds),
    );
  }

  private buildAIInsightRows(): void {
    this.aiInsightTypesRows = [];
    for (let i = 0; i < this.aiInsightTypes.length; i += 2) {
      const row: GenericLookupDto[] = [this.aiInsightTypes[i]];
      if (this.aiInsightTypes[i + 1]) {
        row.push(this.aiInsightTypes[i + 1]);
      }
      this.aiInsightTypesRows.push(row);
    }
  }
}
